import { useState, useEffect } from "react";
import { bannedDomains } from "../../data/bannedDomains";
import {
    Dialog,
    DialogTitle,
    IconButton,
    useTheme,
    Box,
    Typography,
    TextField,
    useMediaQuery,
    Button,
    Stack,
    Tooltip,
    InputAdornment,
} from "@mui/material";
import {
    ArrowBack,
    Close,
    Input
} from "@mui/icons-material";
import logo from "./../../assets/images/newLogo.png";
import poisum from "./../../assets/images/poisum.png";
import toast from "react-hot-toast";
import axios from "axios";
import { useApiClient } from "../../context/ApiClientContext";
import { useNavigate } from "react-router-dom";
import api from "../../helper/baseApi";
import ErrorAlert from "../../helper/errorAlert";
import PublicLayout from "../../layouts/publicLayout";
import ComponentBackdrop from "../../components/backdrop";

const ForgotPassword = ({ open, handleClose }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const labelWidth = 170;
    const [sendPw, setSendPw] = useState(false);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false)

    // const { accountMgtApiUrl } = useApiClient();
    const {
        apiUrls: { accountMgtApiUrl },
    } = useApiClient();


    useEffect(() => {

        setSendPw(false);
        setEmailError("");
        const savedUsername = localStorage.getItem("email");
        if (savedUsername) {
            setEmail(savedUsername); // restore saved email
        } else {
            setEmail(""); // fallback if nothing saved
        }
    }, []);

    const handleChange = (event) => {
        const value = event.target.value;

        setEmail(value);
        const error = validateEmail(value);
        setEmailError(error);
    };

    const validateEmail = (value) => {
        if (!value) {
            return "Email is required.";
        }

        const domain = value.split("@")[1];

        if (bannedDomains.includes(domain)) {
            return `Emails from ${domain} are not allowed. Use your work email address.`;
        }

        return ""; // valid
    };

    const validate = (name, value) => {
        switch (name) {
            case "forgotPwEmail":
                const validateErr = validateEmail(value);
                return validateErr;
            default:
                return "";
        }
    };

    const handleSendEmail = async (event) => {
        event.preventDefault();

        const error = validate("forgotPwEmail", email);
        setEmailError(error);
        if (error !== "") {
            return;
        }

        setOpenBackdrop(true);
        try {
            const response = await api.post(
                `${accountMgtApiUrl}SourceUser/ForgotPassword`,
                {
                    username: email,
                    client_id: "poisum-mobile",
                }
            );

            if (response.status === 200) {
                setSendPw(true);
                setTimeout(() => {
                    setOpenBackdrop(false);
                    toast.success("We've sent an email containing your temporary password to your account. Please log in again.");
                    navigate("/verifyEmail");
                }, 2000);
            }
        } catch (error) {
            setTimeout(() => {
                setOpenBackdrop(false);
                ErrorAlert(error, navigate);
            }, 2000);
        }
    };

    return (
        <PublicLayout>
            <ComponentBackdrop openBackdrop={openBackdrop} />
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%", // Changed from 100vh to 100%
                        maxWidth: { xs: "100%", md: 1100 },
                        overflowY: "auto",
                        boxShadow: { xs: "none", md: "0px 4px 20px rgba(0, 0, 0, 0.1)" },
                        backgroundColor: 'background.main',
                        borderRadius: { xs: "10px", md: "15px" },
                        py: { xs: 4, sm: 8, md: 6 },
                        px: { xs: 1, sm: 2, md: 12 },
                        mx: { xs: 0, md: 2 },
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                        minHeight: { xs: "50vh", md: "auto" },
                        height: { xs: "100%", sm: "90%", md: "650px" },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" }, // Stack on mobile, row on desktop
                            alignItems: "center",
                            justifyContent: "space-around",
                            width: "100%",
                            gap: { xs: 2, md: 9 },
                        }}
                    >
                        {/* Logo Section */}
                        <Box
                            sx={{
                                display: "flex",
                                position: "relative",
                                backgroundColor: "#ecece4a1", // light background
                                borderRadius: "15px",
                                p: { xs: 3, sm: 4, md: 5 }, // responsive padding
                                width: "100%",
                                maxWidth: { xs: 280, sm: 300, md: 450 }, // responsive width
                                mx: "auto",
                                height: { xs: 120, sm: 200, md: 300 }, // responsive height
                            }}
                        >
                            {/* Faint Logo in Background */}
                            <Box
                                component="img"
                                src={logo}
                                alt="Logo"
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: {
                                        xs: 120, // smaller on mobile
                                        sm: 160,
                                        md: 200,
                                        lg: 250,
                                    },
                                    height: "auto",
                                    opacity: 0.08,
                                    zIndex: 0,
                                }}
                            />

                            {/* Foreground Content */}
                            <Stack
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    textAlign: "left",
                                    justifyContent: "center",
                                    height: "100%",
                                }}
                            >
                                <Typography variant={isMobile ? "body" : "subtitle"} sx={{ fontWeight: "bold", color: "#8C8C8C" }}>
                                    Forgot your password? Don’t worry —
                                </Typography>
                                <Typography variant={isMobile ? "body" : "subtitle"} sx={{ fontWeight: "bold", color: "#8C8C8C" }}>
                                    we’ll help you reset it.
                                </Typography>
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                backgroundColor: "#39483f43",
                                borderRadius: '15px',
                                py: { xs: 2, sm: 3, md: 4 },
                                px: { xs: 2, sm: 5, md: 6 },
                                width: { xs: "100%", sm: "70%", md: "380px" },
                                minHeight: { xs: "100%", sm: "90%", md: "450px" },
                                // maxWidth: "480px",
                            }}
                        >
                            <Typography
                                variant="title"
                                display="block"
                                fontWeight={600}
                                gutterBottom
                                textAlign="center"
                                mb={3}
                                textTransform={'uppercase'}
                                sx={{ color: '#fff' }}
                            >
                                POISUM portal
                            </Typography>

                            <Typography
                                variant="subtitle"
                                display="block"
                                fontWeight={600}
                                gutterBottom
                                textAlign="center"
                                mb={3}
                                mt={3}
                                textTransform={'uppercase'}
                                sx={{ color: '#fff' }}
                            >
                                FORGOT PASSWORD
                            </Typography>

                            {/* {!sendPw ? */}
                            <Box
                                component="form"
                                onSubmit={handleSendEmail}
                                sx={{ width: "100%" }}
                                autoComplete="off"
                            >
                                <Box>
                                    <Typography variant="body" display="block"
                                        sx={{ minWidth: isMobile ? 0 : labelWidth, mb: 2, color: '#fff', mt: 2 }}
                                    >
                                        Email:
                                    </Typography>
                                    <TextField
                                        id="forgotPwEmail"
                                        name="forgotPwEmail"
                                        type="email"
                                        autoComplete="off"
                                        value={email}
                                        onChange={(e) => handleChange(e)}
                                        error={!!emailError}
                                        helperText={emailError}
                                        size="normal"
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Back" arrow>
                                                            <IconButton
                                                                onClick={() => navigate(-1)}
                                                                edge="end"
                                                            >
                                                                <ArrowBack />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }

                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "white",
                                                borderRadius: 2,
                                                // "& fieldset": {
                                                //     borderColor: "transparent", // remove border if needed
                                                // },
                                                "&:hover fieldset": {
                                                    borderColor: "primary.light", // optional on hover
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "primary.main", // optional on focus
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    sx={{ backgroundColor: "primary.main", mt: 3, textTransform: "capitalize", fontSize:14 }}
                                >
                                    Send Email
                                </Button>
                            </Box>
                            {/* :
                            <Typography
                                sx={{ minWidth: isMobile ? 0 : labelWidth, mb: 5, color: '#fff', mt: 3, textAlign: 'center' }}
                            >
                                We've sent an email containing your temporary password to your account. Please log in again.
                            </Typography> */}
                            {/* } */}
                        </Box>
                    </Box>
                </Box>
                <Box
                    component="img"
                    src={poisum}
                    alt="poisum"
                    sx={{
                        width:"100%",
                        maxWidth:{ xs: "100%", md: 1000 },
                        mt: { xs: -1.5, sm: -3.5, md: -6 }, // keep slight overlap
                        mx: "auto",
                        display: "block",
                        opacity: 0.9, // optional like your screenshot
                    }}
                />

        </PublicLayout>
    );
}

export default ForgotPassword;